import React, { useCallback, useEffect, useState } from 'react'

// Use
import useMounted from 'use/mounted'
import { useDispatch, useSelector } from 'react-redux'

// Actions
import { setFilters, updateFilters } from 'store/modules/dashboard'

// Api
import {
  getListLessors,
  getListCompanies,
  getListVendors,
  getListContracts,
} from 'api/lessor'

// Components
import Text from 'ui/Text'
import Row from 'ui/Row'
import Col from 'ui/Col'
import Select from 'ui/Form/Select'

export const ContractFilter = () => {
  const dispatch = useDispatch()
  const isMounted = useMounted()
  const filters = useSelector((state) => state.dashboard.filters)
  const [first, setFirst] = useState(true)
  const [lessorLoading, setLessorLoading] = useState(false)
  const [lessorOptions, setLessorOptions] = useState([])
  const [companyLoading, setCompanyLoading] = useState(false)
  const [companyOptions, setCompanyOptions] = useState([])
  const [vendorLoading, setVendorLoading] = useState(false)
  const [vendorOptions, setVendorOptions] = useState([])
  const [contractLoading, setContractLoading] = useState(false)
  const [contractOptions, setContractOptions] = useState([])

  // Get options
  const getContractOptions = useCallback(
    async ({ lessorId, companyId, vendorId }) => {
      setContractOptions([])

      if (lessorId && companyId && vendorId) {
        try {
          setContractLoading(true)

          const data = await getListContracts({ lessorId, companyId, vendorId })

          if (isMounted.current) {
            setContractOptions(data)

            if (data.length === 1) {
              dispatch(
                updateFilters({
                  contractNumber: data[0].value,
                })
              )
            }
          }
        } catch (error) {
          //
        } finally {
          if (isMounted.current) {
            setContractLoading(false)
          }
        }
      }
    },
    [dispatch, isMounted]
  )

  const getVendorOptions = useCallback(
    async ({ lessorId, companyId }) => {
      setVendorOptions([])
      setContractOptions([])

      if (lessorId && companyId) {
        try {
          setVendorLoading(true)

          const data = await getListVendors({ lessorId, companyId })

          if (isMounted.current) {
            setVendorOptions(data)

            // Auto select
            if (data.length === 1) {
              dispatch(
                updateFilters({
                  vendorId: data[0].value,
                  vendorCode: data[0].label,
                })
              )
            }

            // Clear
            if (first === false) {
              dispatch(
                updateFilters({
                  contractNumber: null,
                })
              )
            }

            // Get next select options
            getContractOptions({
              lessorId,
              companyId,
              vendorId:
                data.length === 1
                  ? data[0].value
                  : first
                  ? filters.vendorId
                  : null,
            })
          }
        } catch (error) {
          //
        } finally {
          if (isMounted.current) {
            setVendorLoading(false)
          }
        }
      }
    },
    [dispatch, filters.vendorId, first, getContractOptions, isMounted]
  )

  const getCompanyOptions = useCallback(
    async (lessorId) => {
      setCompanyOptions([])
      setVendorOptions([])
      setContractOptions([])

      if (lessorId) {
        try {
          setCompanyLoading(true)

          const data = await getListCompanies(lessorId)

          if (isMounted.current) {
            setCompanyOptions(data)

            // Auto select
            if (data.length === 1) {
              dispatch(
                updateFilters({
                  companyId: data[0].value,
                  companyCode: data[0].label,
                })
              )
            }

            // Clear
            if (first === false) {
              dispatch(
                updateFilters({
                  vendorId: null,
                  vendorCode: null,
                  contractNumber: null,
                })
              )
            }

            // Get next select options
            getVendorOptions({
              lessorId,
              companyId:
                data.length === 1
                  ? data[0].value
                  : first
                  ? filters.companyId
                  : null,
            })
          }
        } catch (error) {
          //
        } finally {
          if (isMounted.current) {
            setCompanyLoading(false)
          }
        }
      }
    },
    [dispatch, filters.companyId, first, getVendorOptions, isMounted]
  )

  const getLessorOptions = useCallback(async () => {
    try {
      setLessorOptions([])
      setCompanyOptions([])
      setVendorOptions([])
      setContractOptions([])

      setLessorLoading(true)

      const data = await getListLessors()

      if (isMounted.current) {
        setLessorOptions(data)

        // Auto select
        if (data.length === 1) {
          dispatch(
            updateFilters({
              lessorId: data[0].value,
              lessorName: data[0].label,
            })
          )
        }

        // Clear
        if (first === false) {
          dispatch(
            updateFilters({
              companyId: null,
              companyCode: null,
              vendorId: null,
              vendorCode: null,
              contractNumber: null,
            })
          )
        }

        getCompanyOptions(data.length === 1 ? data[0].value : filters.lessorId)
      }
    } catch (error) {
      //
    } finally {
      if (isMounted.current) {
        setLessorLoading(false)
      }
    }
  }, [dispatch, filters.lessorId, first, getCompanyOptions, isMounted])

  // Events
  const onLessorChange = useCallback(
    (value) => {
      const option = lessorOptions.find((x) => x.value === value)

      dispatch(
        setFilters({
          lessorId: value,
          lessorName: option ? option.label : null,
          companyId: null,
          companyCode: null,
          vendorId: null,
          vendorCode: null,
          contractNumber: null,
        })
      )

      getCompanyOptions(value)
    },
    [dispatch, getCompanyOptions, lessorOptions]
  )

  const onCompanyChange = useCallback(
    (value) => {
      const option = companyOptions.find((x) => x.value === value)

      dispatch(
        updateFilters({
          companyId: value,
          companyCode: option ? option.label : null,
          vendorId: null,
          vendorCode: null,
          contractNumber: null,
        })
      )

      getVendorOptions({ lessorId: filters.lessorId, companyId: value })
    },
    [companyOptions, dispatch, filters.lessorId, getVendorOptions]
  )

  const onVendorChange = useCallback(
    (value) => {
      const option = vendorOptions.find((x) => x.value === value)

      dispatch(
        updateFilters({
          vendorId: value,
          vendorCode: option ? option.label : null,
          contractNumber: null,
        })
      )

      setContractOptions([])
      getContractOptions({
        lessorId: filters.lessorId,
        companyId: filters.companyId,
        vendorId: value,
      })
    },
    [
      dispatch,
      filters.companyId,
      filters.lessorId,
      getContractOptions,
      vendorOptions,
    ]
  )

  const onContractChange = useCallback(
    (value) => {
      dispatch(
        updateFilters({
          contractNumber: value,
        })
      )
    },
    [dispatch]
  )

  // Init
  useEffect(() => {
    if (first) {
      setFirst(false)
      getLessorOptions()
    }
  }, [first, getLessorOptions])

  return (
    <>
      <Text bold color="#000000" size={16}>
        Select contract to show:
      </Text>

      <Row>
        <Col xs={12} sm={6} md={4}>
          <Select
            value={filters.lessorId}
            loading={lessorLoading}
            label="Lessor name"
            placeholder="Select here"
            options={lessorOptions}
            disabled={lessorOptions.length < 1}
            onChange={onLessorChange}
          />
        </Col>

        <Col xs={6} sm={3} md={2}>
          <Select
            value={filters.companyId}
            loading={companyLoading}
            label="Company code"
            placeholder="Select here"
            options={companyOptions}
            disabled={companyOptions.length <= 1}
            onChange={onCompanyChange}
          />
        </Col>

        <Col xs={6} sm={4} md={3}>
          <Select
            value={filters.vendorId}
            loading={vendorLoading}
            label="Vendor code"
            placeholder="Select here"
            options={vendorOptions}
            disabled={vendorOptions.length <= 1}
            onChange={onVendorChange}
          />
        </Col>

        <Col xs={6} sm={4} md={3}>
          <Select
            value={filters.contractNumber}
            loading={contractLoading}
            label="Contract no."
            placeholder="Select here"
            options={contractOptions}
            disabled={contractOptions.length <= 1}
            onChange={onContractChange}
          />
        </Col>
      </Row>
    </>
  )
}

ContractFilter.propTypes = {}

ContractFilter.defaultProps = {}

export default ContractFilter
